// Modified App.js with token validation
import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import Navbar from "./components/common/Navbar";
import Footer from "./components/common/Footer";
import SidebarLayout from "./pages/admin_dashboard/admindashboard";
import { ProtectedRoute } from "./components/common/ProtectedRoute";
import { WatchlistProvider } from "./services/contextAPi/watchlistContext";
import { NotificationProvider } from "./contexts/notification/NotificationContext";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { ScreenerProvider } from "./contexts/notification/ScreenerContext";
import { ToastContainer } from "react-toastify";
import { routes } from "./config/routes.config";
import { NavigationProvider } from "./contexts/notification/NavigationContext";
import ScrollToTop from "./components/common/ScrollToTop";
import PageTracker from "./components/common/PageTracker";
import WhatsAppButton from "./components/common/WhatsAppButton";



import { Loader } from "lucide-react";

import { TokenProvider, useToken } from "./services/contextAPi/TokenContext";
import { isTokenValid } from "./services/utils/helpers/tokenUtils";

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-screen">
    <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
  </div>
);

// Title updater component
const TitleUpdater = () => {
  const location = useLocation();

  useEffect(() => {
    // Find the current route
    const currentRoute = routes.find((route) => {
      // Handle exact matches
      if (route.path === location.pathname) return true;

      // Handle dynamic routes with parameters
      if (route.path.includes(":")) {
        const routePathSegments = route.path.split("/");
        const currentPathSegments = location.pathname.split("/");

        if (routePathSegments.length !== currentPathSegments.length)
          return false;

        return routePathSegments.every((segment, index) => {
          return (
            segment.startsWith(":") || segment === currentPathSegments[index]
          );
        });
      }

      // Handle wildcard routes
      if (route.path.includes("*")) {
        const basePath = route.path.replace("/*", "");
        return location.pathname.startsWith(basePath);
      }

      return false;
    });

    // Update document title
    if (currentRoute) {
      document.title = `InvestHive - ${currentRoute.name}`;
    } else {
      document.title = "InvestHive";
    }
  }, [location]);

  return null;
};

// Initial token validation before app loads
const InitialTokenValidator = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    // Only validate if a token exists
    if (token) {
      if (!isTokenValid(token)) {

        localStorage.removeItem("authToken");

        // Only redirect if on a protected route
        const currentPath = location.pathname;
        if (
          currentPath.startsWith("/dashboard") ||
          currentPath.startsWith("/expertdashboard")
        ) {
          navigate("/Signin-Signup");
        }
      }
    }
    // If no token exists, do nothing - let the user browse freely
  }, [navigate, location.pathname]);

  return null;
};
// Authentication check and routing
const AppContent = () => {
  const { isAuthenticated, userRole, loading } = useAuth();
  const { isValidToken, isCheckingToken } = useToken();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect to appropriate dashboard if already logged in
  useEffect(() => {
    if (isAuthenticated && !loading && isValidToken) {
      // Get the current path
      const currentPath = location.pathname;

      // If on login page or root path, redirect based on role
      if (currentPath === "/Signin-Signup" || currentPath === "/") {
        switch (userRole) {
          case "admin":
            navigate("/dashboard/admin/all-users");
            break;
          case "expert":
            navigate("/expertdashboard");
            break;
          default:
            navigate("/dashboard");
            break;
        }
      }
    }
  }, [
    isAuthenticated,
    userRole,
    loading,
    isValidToken,
    navigate,
    location.pathname,
  ]);

  if (loading || isCheckingToken) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <Navbar />
      <div className={`min-h-screen ${isAuthenticated && userRole === 'user' ? 'pb-24 md:pb-0' : ''}`}>
        <Suspense fallback={<LoadingSpinner />}>
          <ScreenerProvider>
            <Routes>
              {/* Standard routes from config */}
              {routes.map(({ path, element: Element, name }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    typeof Element === "function" ? <Element /> : <Element />
                  }
                />
              ))}

              {/* Special handling for admin dashboard routes with new URL structure */}
              <Route
                path="/dashboard/admin"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Navigate to="/dashboard/admin/all-users" replace />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard/admin/:section"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <SidebarLayout />
                  </ProtectedRoute>
                }
              />

              {/* Redirect to home if no routes match */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ScreenerProvider>
        </Suspense>
      </div>
      <Footer />
      <WhatsAppButton />
      <TitleUpdater />
    </>
  );
};

// Root app component with all providers
const AppWithProviders = () => {
  return (
    <TokenProvider>
      <AuthProvider>
        <NotificationProvider>
          <NavigationProvider>
            <WatchlistProvider>
              <InitialTokenValidator />
              <PageTracker />
              <AppContent />
              <ToastContainer position="bottom-right" autoClose={5000} />
            </WatchlistProvider>
          </NavigationProvider>
        </NotificationProvider>
      </AuthProvider>
    </TokenProvider>
  );
};

// Main App component
const App = () => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppWithProviders />
    </BrowserRouter>
  );
};

export default App;
